import { ThemeSchemaResponse } from '@api/api/theme-overview';
import { SkeletonLoader } from '@design/components/skeleton-loader/SkeletonLoader';
import { useNavigateToThemeOverview } from '@terminal/hooks/use-navigate-to-theme-overview/useNavigateToThemeOverview';
import { POSTHOG_TRACKING_SOURCES } from '@terminal/services/tracking/posthog/constants';
import { handlePriceData } from '@terminal/widgets/earnings/utils/tableUtils';
import { ThemeOverviewDrawer } from '@terminal/widgets/theme-widget/theme-overview-drawer/ThemeOverviewDrawer';
import { clsx } from 'clsx';
import { useMemo } from 'react';
import styles from './EarningsSummary.module.scss';
import { EarningsReadThrough } from './earnings-read-through/EarningsReadThrough';
import { EarningsReportingSummary } from './earnings-reporting-summary/EarningsReportingSummary';
import { useReportedCompaniesGuidance } from './reported-companies/useReportedCompaniesGuidance';
import { ThemeEarningsReportingDates } from './ThemeEarningsReportingDates';
import { ThemeKeyStats } from './theme-key-stats/ThemeKeyStats';
import { useEarningsThemeDrawer } from './useEarningsThemeDrawer';
import { useThemeEarningsData } from './useThemeEarningsData';
import {
  type CalendarQuarterPeriod,
  filterEarningsByCalendarPeriod,
  findReportingPeriod,
  getGuidanceStat,
  getMedian,
  getSurpriseStat,
  isPastReportingPeriod,
  splitReportingSummaryRows,
} from './utils';

const DRAWER_SKELETON_AREAS = Array.from({ length: 6 }, () => ({
  className: styles.drawerSkeletonArea,
}));

type EarningsSummaryProps = {
  theme: ThemeSchemaResponse;
  activeReportingPeriod?: CalendarQuarterPeriod;
} & (
  | {
      variant?: 'page';
      onActiveReportingPeriodChange: (
        reportingPeriod: CalendarQuarterPeriod
      ) => void;
    }
  | {
      variant: 'drawer';
      onActiveReportingPeriodChange?: never;
    }
);

export const EarningsSummary = ({
  theme,
  variant = 'page',
  activeReportingPeriod: selectedReportingPeriod,
  onActiveReportingPeriodChange,
}: EarningsSummaryProps) => {
  const { handleOpenThemeDrawer, themeDrawerProps } = useEarningsThemeDrawer();
  const { navigateToThemeOverview } = useNavigateToThemeOverview();
  const { earningsData, reportingPeriods, isError, error, isLoading } =
    useThemeEarningsData(theme.id);
  const isDrawer = variant === 'drawer';

  const activeReportingPeriod = findReportingPeriod(
    reportingPeriods,
    selectedReportingPeriod
  );

  const activeQuarterEarnings = useMemo(
    () => filterEarningsByCalendarPeriod(earningsData, activeReportingPeriod),
    [earningsData, activeReportingPeriod]
  );

  const reportedEarnings = useMemo(
    () => activeQuarterEarnings.filter(earning => earning.reported),
    [activeQuarterEarnings]
  );

  const guidanceByEarningsId = useReportedCompaniesGuidance(reportedEarnings);

  if (isError) {
    throw new Error(error?.message);
  }

  if (isLoading) {
    return (
      <SkeletonLoader
        className={
          isDrawer
            ? styles.drawerSkeleton
            : clsx(styles.skeletonLoaderWrapper, styles.fillViewport)
        }
        areas={
          isDrawer
            ? DRAWER_SKELETON_AREAS
            : [
                {
                  styles: {
                    width: '1280px',
                    height: '100%',
                    margin: '20px auto',
                    borderRadius: '8px',
                  },
                },
              ]
        }
        data-testid="earnings-loading"
      />
    );
  }

  const showReportingSummary =
    !!activeReportingPeriod && !isPastReportingPeriod(activeReportingPeriod);
  const { reportedToday, reportingThisWeek } = splitReportingSummaryRows(
    activeQuarterEarnings
  );

  const epsStat = getSurpriseStat(reportedEarnings, 'eps');
  const revenueStat = getSurpriseStat(reportedEarnings, 'revenue');
  const guidanceStat = getGuidanceStat(reportedEarnings, guidanceByEarningsId);
  // The aggregated (median) 1D % change after reporting; these are static
  // values from the earnings response, not ticking live prices.
  const median1DReaction = getMedian(
    reportedEarnings
      .map(earning => handlePriceData(earning.price, 'd1'))
      .filter((change): change is number => change !== undefined)
  );

  const handleTopicClick = (themeId: string) => {
    if (isDrawer) {
      navigateToThemeOverview(
        themeId,
        POSTHOG_TRACKING_SOURCES.THEME_EARNINGS_DRAWER
      );
      return;
    }

    handleOpenThemeDrawer(themeId);
  };

  return (
    <div
      className={clsx(styles.root, {
        [styles.fillViewport]: !isDrawer,
      })}
      data-testid="earnings-summary"
    >
      {!isDrawer && onActiveReportingPeriodChange && (
        <ThemeEarningsReportingDates
          reportingPeriods={reportingPeriods}
          activeReportingPeriod={activeReportingPeriod}
          onActiveReportingPeriodChange={onActiveReportingPeriodChange}
        />
      )}
      {!isDrawer && <ThemeOverviewDrawer {...themeDrawerProps} />}
      <div className={isDrawer ? styles.drawerContent : styles.scrollContent}>
        <ThemeKeyStats
          variant={variant}
          activeReportingPeriod={activeReportingPeriod}
          reportedCount={reportedEarnings.length}
          totalCount={activeQuarterEarnings.length}
          epsStat={epsStat}
          revenueStat={revenueStat}
          guidanceStat={guidanceStat}
          median1DReaction={median1DReaction}
        />
        <EarningsReadThrough
          themeId={theme.id}
          reportedEarnings={reportedEarnings}
          activeReportingPeriod={activeReportingPeriod}
          onTopicClick={handleTopicClick}
          variant={variant}
        />
        {showReportingSummary && (
          <EarningsReportingSummary
            reportedToday={reportedToday}
            reportingThisWeek={reportingThisWeek}
            guidanceByEarningsId={guidanceByEarningsId}
            onTopicClick={handleTopicClick}
            variant={variant}
          />
        )}
      </div>
    </div>
  );
};
